/**
 * Khối Concept của một ca trên các màn CHỈ ĐỌC — tên + mood + dải ảnh thật.
 *
 * Ô CHỌN concept (ConceptPicker) cho lễ tân xem ảnh từ QĐ 0096, nhưng người
 * PHẢI dựng lại buổi chụp — thợ chụp, trang điểm — chỉ đọc được một dòng chữ.
 * Tên concept không tả được ánh sáng và tông màu; ảnh thì có.
 *
 * Hình dạng do CEO chốt 25/09/2026 (phương án B + bốn chỉnh):
 * - ảnh giữ ĐÚNG TỈ LỆ GỐC — cố định chiều CAO, thả chiều rộng, nên ảnh ngang
 *   ra ngang và ảnh dọc ra dọc. Ép mọi ảnh vào một khung cố định (`object-cover`
 *   như thẻ lookbook) là cắt mất trán với cằm của mẫu — đúng cái CEO chê xấu.
 * - viền vuông nhọn, không bo góc
 * - khe giữa các ảnh hẹp (4px), như một dải phim
 * - cỡ ảnh gấp đôi bản đầu: cao 220px
 *
 * Vì tỉ lệ ảnh chỉ biết được sau khi tải (kho không trả kích thước), khung đặt
 * `min-w` bằng bề rộng một ảnh dọc chuẩn để dải không co về 0 rồi giật ra.
 */
import { useState } from "react";
import { Images } from "lucide-react";

import { imageUrl } from "@/lib/upload";
import { sortImages } from "@/features/lookbook/helpers";
import { useConceptView } from "@/features/lookbook/useConceptView";
import { ConceptGalleryDialog } from "./ConceptGalleryDialog";

/** Cao 220px = cỡ CEO chốt; 147px là bề rộng ảnh dọc 2:3 ở chiều cao đó. */
const CAO = "h-[220px]";
const RONG_TOI_THIEU = "min-w-[147px]";

type Props = {
  /** Mã concept lưu trên đơn (`orders.selected_concept`). */
  conceptId: string | null | undefined;
};

export function ConceptBlock({ conceptId }: Props) {
  const { nameOf, conceptOf } = useConceptView();
  const [galleryOpen, setGalleryOpen] = useState(false);

  const name = nameOf(conceptId);
  if (!name) return null;

  const concept = conceptOf(conceptId);
  const images = concept ? sortImages(concept.images) : [];
  const mood = [concept?.tone, concept?.mood].map((s) => (s ?? "").trim()).filter(Boolean).join(" · ");

  return (
    <div
      className="overflow-hidden rounded-xl border p-3"
      style={{ background: "var(--color-surface-2)", borderColor: "var(--color-border)" }}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span
            className="shrink-0 text-[10px] font-bold uppercase tracking-wider"
            style={{ color: "var(--color-text-secondary)" }}
          >
            Concept
          </span>
          <span className="truncate text-[15px] font-bold">{name}</span>
          {mood && (
            <span className="truncate text-xs" style={{ color: "var(--color-text-secondary)" }}>
              {mood}
            </span>
          )}
        </div>
        {images.length > 0 && (
          <button
            type="button"
            className="shrink-0 text-xs font-semibold"
            style={{ color: "var(--color-primary)" }}
            onClick={() => setGalleryOpen(true)}
          >
            Xem tất cả →
          </button>
        )}
      </div>

      {images.length === 0 ? (
        <p className="flex items-center gap-1.5 text-xs" style={{ color: "var(--color-text-secondary)" }}>
          <Images className="h-3.5 w-3.5" aria-hidden />
          Concept này chưa gắn ảnh
        </p>
      ) : (
        <div className="flex gap-1 overflow-x-auto pb-1">
          {images.map((img) => (
            <button
              key={img.id}
              type="button"
              className={`${CAO} ${RONG_TOI_THIEU} shrink-0 border`}
              style={{ borderColor: "var(--color-border)", background: "var(--color-surface-3)" }}
              aria-label={`Phóng to ảnh concept ${name}`}
              onClick={() => setGalleryOpen(true)}
            >
              {/* alt rỗng có chủ ý: tên concept nằm ngay trên dải ảnh. */}
              <img
                data-testid="concept-block-thumb"
                src={imageUrl(img.url, "md")}
                alt=""
                loading="lazy"
                className={`${CAO} w-auto object-contain`}
              />
            </button>
          ))}
        </div>
      )}

      {concept && (
        <ConceptGalleryDialog open={galleryOpen} concepts={[concept]} onClose={() => setGalleryOpen(false)} />
      )}
    </div>
  );
}
